import { coverageFor, reportsPosition, sumRows } from '../GscTools/metrics';
import { SEGMENT, SEGMENTS, SEGMENT_OPERATOR, knownQueryShare } from './brand';

/**
 * The figures the blocks draw, derived from what came back rather than fetched again.
 *
 * Kept out of the component so the arithmetic that matters on this report — what the share is
 * divided by, and what counts as a known-query total — is in one readable place instead of spread
 * through render methods.
 */

/**
 * Block 02's two rows: each side's totals, its movement, and its share of the classified clicks.
 *
 * The share is computed from both sides' current clicks and is withheld whenever that is not
 * possible: a side that did not come back leaves the denominator incomplete, and two sides that
 * both returned nothing leave it at zero. Neither is reported as 0% — a share of nothing is not
 * a small share.
 *
 * `comparable` is set only where both windows returned the side, which is what makes the shared
 * comparison columns print the movement rather than "not comparable". Search Console returning
 * nothing for a window is not the same as the side having had no traffic in it.
 */
export function comparisonRows(scope, totals) {
    const clicksOf = (segment) => totals[segment]?.current?.clicks ?? null;

    const brandClicks = clicksOf(SEGMENT.BRAND);
    const nonBrandClicks = clicksOf(SEGMENT.NON_BRAND);

    const bothSides = brandClicks !== null && nonBrandClicks !== null;
    const anyClicks = bothSides && brandClicks + nonBrandClicks > 0;

    const shareReason = !bothSides ? 'side_missing' : !anyClicks ? 'no_clicks' : null;

    return SEGMENTS.filter((segment) => totals[segment]?.status === 'done').map((segment) => {
        const current = totals[segment]?.current ?? null;
        const prior = totals[segment]?.baseline ?? null;
        const comparable = !!current && !!prior;

        const share =
            segment === SEGMENT.BRAND
                ? knownQueryShare(brandClicks, nonBrandClicks)
                : knownQueryShare(nonBrandClicks, brandClicks);

        return {
            id: segment,
            key: segment,
            // Carried on the row so the export says which filter produced it without the reader
            // having to remember which side is which operator.
            operator: `query ${SEGMENT_OPERATOR[segment]}`,
            current,
            prior,
            comparable,
            deltaClicks: comparable ? current.clicks - prior.clicks : null,
            deltaPercent:
                comparable && prior.clicks > 0
                    ? (100 * (current.clicks - prior.clicks)) / prior.clicks
                    : null,
            share,
            shareReason,
        };
    });
}

/**
 * Block 03's headline numbers: the two sides added together, over the days that came back.
 *
 * This is the one total the two sides may be added into, because they are complementary by
 * construction — the same expression as `includingRegex` and as `excludingRegex` — so no query
 * can be in both and nothing is double counted. It is still not the property's total: the queries
 * Search Console withheld are in neither side, which is exactly why the cards call it the
 * known-query total and the block says what that excludes.
 *
 * The sum goes through the shared row totals, so CTR is the summed clicks over the summed
 * impressions and average position is impression-weighted rather than either being averaged
 * across sides — a mean of two sides' CTRs would weight a small side like a large one.
 *
 * A day is covered when either side returned it. A day neither returned is unknown for the
 * window as a whole, which is what the comparison is allowed to depend on.
 */
export function knownTotals(scope, daily) {
    const hasPosition = reportsPosition(scope.searchType);

    const forPeriod = (period, key) => {
        if (!period) return null;

        const rows = SEGMENTS.filter((segment) => daily[segment]?.status === 'done').flatMap(
            (segment) => daily[segment]?.[key]?.rows ?? [],
        );

        return { totals: sumRows(rows, hasPosition), coverage: coverageFor(rows, period) };
    };

    return {
        current: forPeriod(scope.current, 'current'),
        baseline: forPeriod(scope.baseline, 'baseline'),
    };
}
